import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlarmClock, ArrowRight, CircleCheckBig, CircleX, Clock, Download, FilePen, Inbox, ListChecks, Printer, RotateCcw, Search, Send, Timer, Trash2,
} from 'lucide-react';
import * as hr from '../../api';
import { hrSubUrl } from '../../catalog';
import { useHR } from '../../HRContext';
import { DateInput } from '../../components/Controls';
import { download, fmtNumber, toCSV } from '../../format';
import { Chip } from '../attendance/shared';
import { Empty, PriorityChip, printHtml, StatusChip, STATUS_LABEL } from './shared';
import Loader from '../../components/Loader';

// HR Request Center: Dashboard, Drafts, Reports and Audit Trail (Stephanet's custom/hrrc/{Dashboard,Drafts,Reports,AuditTrail}.jsx).

function Trend({ t }) {
  if (!t) return null;
  if (t.pct === null) return t.this_month ? <small className="hrc-trend up">↑ {t.this_month} new this month</small> : <small className="hrc-trend">No change</small>;
  return <small className={`hrc-trend ${t.pct > 0 ? 'up' : t.pct < 0 ? 'down' : ''}`}>{t.pct > 0 ? '↑' : t.pct < 0 ? '↓' : '→'} {Math.abs(t.pct)}%</small>;
}

export function KpiCard({ label, value, tone, Ico, trend, note, onClick, i = 0 }) {
  return (
    <button type="button" className={`md-kpi hrc-kpi lc-${tone}`} style={{ animationDelay: `${i * 30}ms` }} onClick={onClick} disabled={!onClick}>
      <div className="md-kpi-head"><span>{label}</span>{Ico && <Ico size={17} />}</div>
      <b>{typeof value === 'number' ? fmtNumber(value) : value}</b>
      {trend ? <Trend t={trend} /> : note ? <small>{note}</small> : null}
    </button>
  );
}

export function Dashboard() {
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => { hr.hrrcDashboard().then(setD).catch(e => setError(e.message)); }, []);
  if (error) return <div className="error">{error}</div>;
  if (!d) return <Loader title="Loading dashboard…" />;
  const k = d.kpis;
  const go = status => navigate(hrSubUrl('hrrc-all', status ? { status } : null));
  return (
    <>
      <div className="md-kpis hrc-kpis5">
        <KpiCard label="Total Requests" value={k.total} tone="blue" Ico={Inbox} trend={d.trends.total} onClick={() => go()} />
        <KpiCard label="Pending Approval" value={k.pending} tone="amber" Ico={Clock} trend={d.trends.pending} onClick={() => go('pending_approval')} i={1} />
        <KpiCard label="Approved" value={k.approved} tone="green" Ico={CircleCheckBig} trend={d.trends.approved} onClick={() => go('approved')} i={2} />
        <KpiCard label="Rejected" value={k.rejected} tone="red" Ico={CircleX} note="Declined" onClick={() => go('rejected')} i={3} />
        <KpiCard label="Overdue" value={k.overdue} tone="rose" Ico={AlarmClock} note="Open past SLA due date" onClick={() => go('overdue')} i={4} />
      </div>
      <div className="md-kpis hrc-kpis5">
        <KpiCard label="Draft Requests" value={k.drafts} tone="slate" Ico={FilePen} note="Not yet submitted" onClick={() => navigate(hrSubUrl('hrrc-drafts'))} i={5} />
        <KpiCard label="Submitted" value={k.submitted} tone="sky" Ico={Send} note={k.in_review ? `${k.in_review} in review` : 'Awaiting first approver'} onClick={() => go('submitted')} i={6} />
        <KpiCard label="Sent Back" value={k.sent_back} tone="orange" Ico={RotateCcw} note="Returned to requester" onClick={() => go('sent_back')} i={7} />
        <KpiCard label="Completed" value={k.completed} tone="emerald" Ico={ListChecks} note="Closed out" onClick={() => go('completed')} i={8} />
        <KpiCard label="Avg Approval Time" value={k.avg_approval_days === null ? '—' : `${k.avg_approval_days}d`} tone="purple" Ico={Timer}
                 note={k.decided ? `Submission to decision · ${k.decided} decided` : 'No decisions yet'} i={9} />
      </div>
      <div className="mdr-card mdr-table-card hrc-mt">
        <div className="mds-table-head"><b>Recent Requests</b><button className="mds-view" onClick={() => go()}>View all</button></div>
        <div className="mdr-scroll">
          <table className="mdr-table mds-table">
            <thead><tr>{['Request No', 'Title', 'Module', 'Requested By', 'Department', 'Status', 'Priority', 'Date'].map(h => <th key={h}>{h}</th>)}</tr></thead>
            <tbody>
              {d.recent.map((r, i) => (
                <tr key={r.id} className="mdr-row" style={{ animationDelay: `${i * 25}ms` }} onClick={() => navigate(hrSubUrl('hrrc-all', { open: r.id }))}>
                  <td className="mono">{r.request_no}</td><td><b>{r.title || '—'}</b></td><td>{r.module_label}</td><td>{r.requested_by}</td>
                  <td>{r.department || '—'}</td><td><StatusChip s={r.status} /></td><td><PriorityChip p={r.priority} /></td><td className="nowrap">{r.date}</td>
                </tr>
              ))}
              {!d.recent.length && <Empty cols={8}>No requests yet. Use New Request to raise the first one.</Empty>}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

export function Drafts() {
  const { notify, confirm } = useHR();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const load = () => hr.hrrcDrafts().then(setD).catch(e => notify(e.message, 'bad'));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  if (!d) return <Loader title="Loading drafts…" />;
  const remove = async r => {
    if (!await confirm({ title: 'Delete draft', message: `Are you sure you want to delete draft “${r.title || r.request_no}”? Its attachments are deleted too.`,
      confirmLabel: 'Delete', danger: true })) return;
    try { await hr.hrrcDelete(r.id); notify(`Draft ${r.request_no} has been deleted successfully`); load(); } catch (e) { notify(e.message, 'bad'); }
  };
  return (
    <div className="mdr-card mdr-table-card">
      <div className="mds-table-head"><b>Draft Requests</b><span className="mds-note-inline">{d.rows.length} draft(s) · completion = filled fields of the request type</span></div>
      <div className="mdr-scroll">
        <table className="mdr-table mds-table">
          <thead><tr>{['Request No', 'Title', 'Module', 'Created By', 'Last Updated', 'Completion %', 'Actions'].map(h => <th key={h}>{h}</th>)}</tr></thead>
          <tbody>
            {d.rows.map((r, i) => (
              <tr key={r.id} className="mdr-row" style={{ animationDelay: `${i * 20}ms` }}>
                <td className="mono">{r.request_no}{r.status !== 'draft' && <div><StatusChip s={r.status} /></div>}</td>
                <td><b>{r.title || 'Untitled request'}</b><small className="ota-sub">{r.request_type || 'Type not chosen'}</small></td>
                <td>{r.module_label || '—'}</td>
                <td>{r.requested_by}</td>
                <td className="nowrap">{r.last_updated}</td>
                <td><div className="hrc-progress"><span className="hrc-bar"><i style={{ width: `${r.completion_pct}%` }} /></span><b>{r.completion_pct}%</b></div></td>
                <td className="nowrap">
                  {(r.mine || d.can.configure) && <button className="mdr-bulk-btn b-outline" onClick={() => navigate(hrSubUrl('hrrc-new', { draft: r.id }))}><ArrowRight size={13} /> Continue</button>}
                  {r.status === 'draft' && (r.mine || d.can.configure) && <button className="hrc-del" onClick={() => remove(r)}><Trash2 size={13} /> Delete</button>}
                </td>
              </tr>
            ))}
            {!d.rows.length && <Empty cols={7}>No drafts. Requests saved with “Save as Draft” appear here.</Empty>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const REPORT_COLS = [['Module', 'area'], ['Total', 'total'], ['Draft', 'draft'], ['Submitted', 'submitted'], ['Pending', 'pending'], ['Approved', 'approved'],
  ['Rejected', 'rejected'], ['Completed', 'completed']];
const REPORT_TONE = { total: 'in', draft: '', submitted: 'in', pending: 'am', approved: 'g', rejected: 'rd', completed: 'g' };

export function Reports() {
  const [range, setRange] = useState({ date_from: '', date_to: '' });
  const [d, setD] = useState(null);
  useEffect(() => { hr.hrrcReports(range).then(setD).catch(() => setD({ rows: [], can: {} })); }, [range]);
  if (!d) return <Loader title="Loading reports…" />;
  const tot = Object.fromEntries(REPORT_COLS.slice(1).map(([, k]) => [k, d.rows.reduce((a, r) => a + r[k], 0)]));
  const print = () => printHtml('Request Reports by Module', `<h1>Request Reports by Module</h1><p class="muted">${range.date_from || 'Start'} to ${range.date_to || 'today'}</p>
    <table><thead><tr>${REPORT_COLS.map(([h]) => `<th>${h}</th>`).join('')}</tr></thead><tbody>${d.rows.map(r => `<tr>${REPORT_COLS.map(([, k]) => `<td>${r[k]}</td>`).join('')}</tr>`).join('')}
    <tr class="tot"><td>Total</td>${REPORT_COLS.slice(1).map(([, k]) => `<td>${tot[k]}</td>`).join('')}</tr></tbody></table>`);
  return (
    <>
      <div className="ts-filters hrc-filters no-print">
        <label className="hrc-inline">From <DateInput value={range.date_from} onChange={e => setRange(r => ({ ...r, date_from: e.target.value }))} /></label>
        <label className="hrc-inline">To <DateInput value={range.date_to} onChange={e => setRange(r => ({ ...r, date_to: e.target.value }))} /></label>
        {(range.date_from || range.date_to) && <button className="mdr-btn" onClick={() => setRange({ date_from: '', date_to: '' })}><RotateCcw size={14} /> Reset</button>}
        <span className="wh-spacer" />
        <button className="mdr-btn" onClick={print} disabled={!d.rows.length}><Printer size={14} /> Print</button>
        <button className="mdr-btn" disabled={!d.rows.length} onClick={() => download('request-reports-by-module.csv', toCSV(REPORT_COLS.map(([label, k]) => ({ label, get: r => r[k] })), d.rows))}><Download size={14} /> Export</button>
      </div>
      <div className="mdr-card mdr-table-card">
        <div className="mds-table-head"><b>Request Reports by Module</b><span className="mds-note-inline">Pending = pending approval + in review</span></div>
        <div className="mdr-scroll">
          <table className="mdr-table mds-table">
            <thead><tr>{REPORT_COLS.map(([h]) => <th key={h} className={h === 'Module' ? '' : 'c'}>{h}</th>)}</tr></thead>
            <tbody>
              {d.rows.map((r, i) => (
                <tr key={r.module} className="mdr-row" style={{ animationDelay: `${i * 25}ms` }}>
                  {REPORT_COLS.map(([, k]) => <td key={k} className={k === 'area' ? 'b' : `c ${REPORT_TONE[k]}`}>{r[k]}</td>)}
                </tr>
              ))}
              {!d.rows.length && <Empty cols={8}>No requests in this period.</Empty>}
            </tbody>
            {d.rows.length > 0 && <tfoot><tr><td>Total</td>{REPORT_COLS.slice(1).map(([, k]) => <td key={k} className="c">{tot[k]}</td>)}</tr></tfoot>}
          </table>
        </div>
      </div>
    </>
  );
}

const AUDIT_COLS = [['Date/Time', 'date'], ['Request No', 'request_no'], ['Action', 'action'], ['Previous Status', 'previous_status'], ['New Status', 'new_status'],
  ['Performed By', 'performed_by'], ['Result', 'result'], ['Comment', 'comment']];
const statusKey = label => Object.keys(STATUS_LABEL).find(k => STATUS_LABEL[k] === label);

export function AuditTrail() {
  const [q, setQ] = useState('');
  const [d, setD] = useState(null);
  useEffect(() => { const t = setTimeout(() => hr.hrrcAudit(q).then(setD).catch(() => setD({ rows: [], can: {} })), 250); return () => clearTimeout(t); }, [q]);
  return (
    <>
      <div className="ts-filters hrc-filters no-print">
        <div className="mdr-search"><Search size={14} /><input placeholder="Search request no, action or user..." value={q} onChange={e => setQ(e.target.value)} /></div>
        <button className="mdr-btn" disabled={!d?.rows.length} onClick={() => download('request-audit-trail.csv', toCSV(AUDIT_COLS.map(([label, k]) => ({ label, get: r => r[k] })), d.rows))}><Download size={14} /> Export</button>
      </div>
      <div className="mdr-card mdr-table-card">
        <div className="mdr-scroll">
          <table className="mdr-table mds-table">
            <thead><tr>{AUDIT_COLS.slice(0, 7).map(([h]) => <th key={h}>{h}</th>)}</tr></thead>
            <tbody>
              {!d && <tr><td colSpan={7}><Loader title="Loading requests…" /></td></tr>}
              {d?.rows.map((e, i) => (
                <tr key={`${e.id}-${i}`} className="mdr-row" style={{ animationDelay: `${Math.min(i, 20) * 15}ms` }} title={e.comment || ''}>
                  <td className="nowrap">{e.date}</td>
                  <td className="mono">{e.request_no}</td>
                  <td><b>{e.action}</b>{e.comment && <small className="ota-sub">{e.comment}</small>}</td>
                  <td>{e.previous_status ? <StatusChip s={statusKey(e.previous_status)} /> : '—'}</td>
                  <td>{e.new_status ? <StatusChip s={statusKey(e.new_status)} /> : '—'}</td>
                  <td>{e.performed_by}</td>
                  <td><Chip tone={e.result === 'Success' ? 'green' : 'red'}>{e.result}</Chip></td>
                </tr>
              ))}
              {d && !d.rows.length && <Empty cols={7}>No audit entries.</Empty>}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
